/**
 * Theme bootstrapping — pure, React-free, so it can be unit-tested under node
 * and reused by the useDarkMode hook.
 *
 * Resolution order (same in the boot script and in the hook):
 *   1. localStorage[THEME_STORAGE_KEY] === 'dark' | 'light'  (explicit choice)
 *   2. prefers-color-scheme: dark                             (system default)
 *
 * THEME_BOOT_SCRIPT is the inline <script> every app's index.html carries in
 * <head> so `data-theme` is set before first paint (no light→dark flash).
 * tests/theme-boot.test.ts asserts the HTML files embed it verbatim.
 */

export const THEME_STORAGE_KEY = 'paz-theme';

export type ThemeMode = 'light' | 'dark';
export type ThemeSource = 'stored' | 'system';

export interface ResolvedTheme {
  isDark: boolean;
  source: ThemeSource;
}

/**
 * Inline boot script. Kept on a single line with no `</script>` sequence so
 * it can be embedded as-is. Mirrors resolveTheme() + applyTheme() below.
 */
export const THEME_BOOT_SCRIPT =
  "(function(){try{var k='" +
  THEME_STORAGE_KEY +
  "';var s=null;try{s=localStorage.getItem(k)}catch(e){}" +
  "var m=!!(window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches);" +
  "var d=s==='dark'||(s!=='light'&&m);var r=document.documentElement;" +
  "if(d){r.setAttribute('data-theme','dark')}else{r.removeAttribute('data-theme')}" +
  "r.style.colorScheme=d?'dark':'light'}catch(e){}})();";

export function readStoredTheme(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : null;
  } catch {
    return null;
  }
}

export function writeStoredTheme(mode: ThemeMode | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (mode === null) window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* storage unavailable (private mode, blocked) — theme still applies for this page */
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(): ResolvedTheme {
  const stored = readStoredTheme();
  if (stored) return { isDark: stored === 'dark', source: 'stored' };
  return { isDark: systemPrefersDark(), source: 'system' };
}

/** Applies `data-theme` + `color-scheme` to <html>. Idempotent. */
export function applyTheme(isDark: boolean): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (isDark) root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
  root.style.colorScheme = isDark ? 'dark' : 'light';
}
